// src/components/ResponsiveButton/NeonButton.jsx
import React from "react";
import PropTypes from "prop-types";

/**
 * HUOM: Emme käytä framer-motionia transform-animaatioihin,
 * jotta Tailwindin scale-[…] luokat (esim. scale-[0.80]) toimivat.
 * Hover/active-efekti tehdään Tailwindilla (translate-y).
 */

const VARIANTS = {
  primary: [
    // min. 16px mobiilissa → ei iOS-zoomia, pienennä ulkoisesti scale-[…]-luokalla
    "text-[16px] sm:text-sm md:text-sm mdd:text-sm text-white text-shadow-sm",
    "bg-gradient-to-r from-pink to-pink",
    "dark:bg-gradient-to-r dark:from-electric/50 dark:to-secondary/20",
    "ring-1 ring-primary/20",
    "shadow-[0_6px_18px_rgba(0,0,0,0.25),0_2px_6px_rgba(0,0,0,0.12)]",
    "hover:shadow-[0_10px_28px_rgba(0,0,0,0.28),0_4px_10px_rgba(0,0,0,0.16)]",
    "dark:shadow-[0_0_20px_rgba(0,0,0,0.45)]",
    "dark:hover:shadow-[0_0_36px_rgba(0,0,0,0.40)]",
  ].join(" "),
  secondary: [
    "text-[16px] sm:text-sm md:text-sm mdd:text-sm font-customFont-1 text-black/90 dark:text-white/90",
    "bg-electric/80 dark:bg-primary/20",
    "ring-1 ring-primary/40",
    "shadow-[0_6px_16px_rgba(0,0,0,0.18),0_2px_4px_rgba(0,0,0,0.12)]",
    "hover:shadow-[0_10px_24px_rgba(0,0,0,0.22),0_4px_8px_rgba(0,0,0,0.14)]",
    "dark:shadow-[0_0_16px_rgba(0,0,0,0.35)]",
    "dark:hover:shadow-[0_0_24px_rgba(0,0,0,0.45)]",
  ].join(" "),
  ghost: [
    "text-[16px] sm:text-sm md:text-sm mdd:text-sm text-black/80 dark:text-white/90",
    "bg-pink/20",
    "ring-1 ring-black/10 hover:ring-black/20 dark:ring-white/30 dark:hover:ring-white/60",
    "shadow-[0_0_14px_rgba(0,0,0,0.12)]",
    "hover:shadow-[0_0_18px_rgba(0,0,0,0.18)]",
    "dark:shadow-[0_0_14px_rgba(255,255,255,0.12)]",
    "dark:hover:shadow-[0_0_18px_rgba(255,255,255,0.20)]",
  ].join(" "),
};

// Korkeus/leveys erotellaan koosta; fonttikoko tulee variantista
const SIZES = {
  xs: "h-8 px-3",
  sm: "h-10 px-4",
  md: "h-12 px-5",
  lg: "h-14 px-6",
};

const SQUARE_SIZES = {
  xs: "h-8 w-8",
  sm: "h-10 w-10",
  md: "h-12 w-12",
  lg: "h-14 w-14",
};

function clsx(...a) { return a.filter(Boolean).join(" "); }

export default function NeonButton({
  as: As = "button",
  children,
  onClick,
  type = "button",
  variant = "primary",
  size = "md",
  fullWidth = false,
  disabled = false,
  loading = false,
  IconLeft,
  IconRight,
  iconOnly = false,
  className = "",
  ...props
}) {
  // Perusrunko: ei inline-transformeja → Tailwindin scale toimii
const base =
  "group relative isolate inline-flex items-center justify-center gap-2 rounded-xl " +
  "font-customFont1 font-normal uppercase tracking-wide leading-none " +
  "transform transform-gpu transition-colors transition-transform duration-300 ease-out " + // ← lisätty 'transform'
  "hover:-translate-y-[1px] active:translate-y-[1px] " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/70 " +
  "ring-offset-transparent select-none";


  const sizeCls = iconOnly ? (SQUARE_SIZES[size] || SQUARE_SIZES.md)
                           : (SIZES[size] || SIZES.md);
  const variantCls = VARIANTS[variant] || VARIANTS.primary;

  const isButton = As === "button";
  const buttonAttrs = isButton ? { type, disabled: disabled || loading } : {};

  return (
    <As
      {...buttonAttrs}
      {...props}
      onClick={disabled || loading ? undefined : onClick}
      aria-busy={loading}
      aria-disabled={!isButton && (disabled || loading) ? true : undefined}
      className={clsx(
        base,
        sizeCls,
        variantCls,
        fullWidth && !iconOnly && "w-full",
        (disabled || loading) && "opacity-60 cursor-not-allowed pointer-events-none",
        className
      )}
    >
      {/* Pehmeä hohde taustalle */}
      <span
        aria-hidden
        className={clsx(
          "pointer-events-none absolute inset-0 -z-10 rounded-xl blur-xl opacity-40",
          variant === "primary"   && "bg-primary/30 group-hover:opacity-60",
          variant === "secondary" && "bg-primary/25 group-hover:opacity-50",
          variant === "ghost"     && "bg-black/10 group-hover:bg-black/15 dark:bg-white/10 dark:group-hover:bg-white/15"
        )}
      />

      {/* “Sheen” -viisto kiilto hoverilla */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-[-40%] w-[40%] skew-x-[-20deg]
                   bg-white/20 dark:bg-white/30 blur-md opacity-0 group-hover:opacity-40
                   group-hover:translate-x-[260%] transition-all duration-700 ease-out rounded-xl"
      />

      {!loading && IconLeft && <IconLeft className="h-5 w-5 block shrink-0" />}
      {!iconOnly && <span className="drop-shadow text-shadow-sm">{loading ? "Ladataan..." : children}</span>}
      {loading
        ? (
          <svg className="h-5 w-5 animate-spin block" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-90" d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
          </svg>
        )
        : (!iconOnly && IconRight && <IconRight className="h-5 w-5 block shrink-0" />)
      }
    </As>
  );
}

NeonButton.propTypes = {
  as: PropTypes.elementType,
  children: PropTypes.node,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  variant: PropTypes.oneOf(["primary", "secondary", "ghost"]),
  size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  IconLeft: PropTypes.elementType,
  IconRight: PropTypes.elementType,
  iconOnly: PropTypes.bool,
  className: PropTypes.string,
};
